import React, { useCallback, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import EditSection from "components/common/ui/EditSection";
import CommonButton from "components/common/ui/CommonButton";
import dayjs, { Dayjs } from "dayjs";
import FoodPurchasingGuideTable from "components/inventory/ui/purchasing/FoodPurchasingGuideTable";
// eslint-disable-next-line max-len
import WeekCalendarMenuView from "components/inventory/ui/cyclePurchasing/WeekMenuViewSubcomponents/WeekCalendarMenuView";
import { useAppDispatch, useAppSelector } from "hooks/useReduxStore";
import menusInWeekSelector from "store/selectors/menu/menusInWeekSelector";
import foodPurchasingGuideSelector from "store/selectors/inventory/foodPurchasingGuideSelector";
import { foodPurchasingGuideActions } from "store/slices/inventory/foodPurchasingGuideSlice";
import {
  MenusOnDateParams,
  ParamFilterPurchaseGuide,
} from "client/jspPlatformExperiment";

interface SelectMenusStepProps {
  onNext: () => void;
  onBack: () => void;
  fromDate: Dayjs;
  toDate: Dayjs;
}

const SelectMenusStep: React.FC<SelectMenusStepProps> = ({
  onNext,
  onBack,
  fromDate,
  toDate,
}) => {
  const selectedMenusByDate = useAppSelector(
    menusInWeekSelector.selectedMenusByDate,
  );
  const allMenusOptions = useAppSelector(menusInWeekSelector.menusInWeek);
  const isMenuInWeekLoading = useAppSelector(
    menusInWeekSelector.isMenusInWeekLoading,
  );
  const menuInWeekType = useAppSelector(
    menusInWeekSelector.menusInWeekMenuType,
  );
  const purchasingGuide = useAppSelector(
    foodPurchasingGuideSelector.foodPurchasingGuide,
  );
  const purchasingGuideError = useAppSelector(
    foodPurchasingGuideSelector.foodPurchasingGuideError,
  );
  const dispatch = useAppDispatch();
  const daysDiff = toDate.diff(fromDate, "day") + 1;

  // Check if this is an à la carte menu (alternate menu)
  const isALaCarteMenu = menuInWeekType === "alternate menu";

  // State for toggling calendar view for à la carte menus
  const [showCalendarView, setShowCalendarView] = useState(!isALaCarteMenu);

  const handleGetPurchaseListByMenus = useCallback(
    (filterParams?: ParamFilterPurchaseGuide | null) => {
      // Check if selectedMenusByDate is empty
      const hasSelectedMenus = Object.values(selectedMenusByDate).some(
        (menuIds) => Array.isArray(menuIds) && menuIds.length > 0,
      );

      let requiredMenuIdsOnDates: MenusOnDateParams[];

      if (hasSelectedMenus) {
        // Use selected menus if any are selected
        requiredMenuIdsOnDates = Object.entries(selectedMenusByDate)
          .filter(
            ([_, menuIds]) => Array.isArray(menuIds) && menuIds.length > 0,
          )
          .map(([date, menuIds]) => ({
            date,
            menu_ids: menuIds,
          }));
      } else {
        // Use all available menus if none are selected
        requiredMenuIdsOnDates =
          allMenusOptions?.map((menu) => ({
            date: dayjs(menu.serving_datetime).format("YYYY-MM-DD"),
            menu_ids: [menu.id],
          })) || [];
      }

      // Get the purchase list by menus on date
      dispatch(
        foodPurchasingGuideActions.fetchFoodPurchasingGuideByMenusOnDate({
          required_menu_ids_on_dates: requiredMenuIdsOnDates,
          reserved_menu_ids_on_dates: undefined,
          filter_params: filterParams ?? undefined,
        }),
      );
    },
    [selectedMenusByDate, allMenusOptions, dispatch],
  );

  return (
    <Box>
      {isALaCarteMenu ? (
        !isMenuInWeekLoading && (
          <Box>
            <CommonButton text="Go back" onClick={onBack} />
            <CommonButton
              text={
                showCalendarView ? "Hide Calendar View" : "Show Calendar View"
              }
              onClick={() => setShowCalendarView(!showCalendarView)}
              positive={showCalendarView}
            />
          </Box>
        )
      ) : (
        <>
          <Typography>
            Select the days and menus for which you want to generate the
            ingredient purchase list.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Date range: {fromDate.format("MMM DD")} -{" "}
            {toDate.format("MMM DD, YYYY")} ({daysDiff} days)
            {!isMenuInWeekLoading && (
              <CommonButton text="Modify date range" onClick={onBack} />
            )}
          </Typography>
        </>
      )}

      <EditSection>
        {(showCalendarView || isMenuInWeekLoading) && <WeekCalendarMenuView />}
        {!isMenuInWeekLoading && (
          <>
            <Typography variant="h6" gutterBottom>
              {(() => {
                const selectedCount = Object.values(selectedMenusByDate).reduce(
                  (acc, menuIds) =>
                    acc + (Array.isArray(menuIds) ? menuIds.length : 0),
                  0,
                );
                return selectedCount > 0
                  ? `Selected Menus: ${selectedCount}`
                  : "Selected Menus: All Menus";
              })()}
            </Typography>
            <FoodPurchasingGuideTable
              onGetPurchaseListClick={handleGetPurchaseListByMenus}
            />
          </>
        )}
      </EditSection>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item>
          <CommonButton
            text="Continue"
            onClick={onNext}
            positive={
              !isMenuInWeekLoading &&
              !!purchasingGuide &&
              purchasingGuide.length !== 0 &&
              !purchasingGuideError
            }
            disabled={
              isMenuInWeekLoading ||
              !purchasingGuide ||
              (!!purchasingGuide && purchasingGuide.length === 0) ||
              !!purchasingGuideError
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default SelectMenusStep;
